<template>
  <Link :to="buildingRoute(building.id)" :decoration="false">
    <Card variant="secondary" size="medium" class="building-card">
      <View gap="md" direction="column">
        <View gap="md">
          <IconTile icon="building-06" size="large" color="neutral" />

          <View direction="column" gap="xs" class="identity">
            <Text variant="h5">{{ building.name }}</Text>
            <Text variant="body-medium" color="secondary">{{ buildingAddressLine(building.location) }}</Text>
          </View>
        </View>

        <View v-if="showPortfolio || showManager" gap="sm" align="center" wrap="wrap" class="team">
          <PortfolioChip
            v-if="showPortfolio && building.portfolio"
            :portfolio="building.portfolio"
            @select="emit('select-portfolio', $event)"
          />
          <View v-if="showManager && building.manager" gap="xs" align="center" class="manager">
            <Avatar :user-name="managerName" show-single-letter size="small" />
            <Text variant="body-small" color="secondary">{{ managerLabel }}</Text>
          </View>
        </View>
      </View>
    </Card>
  </Link>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { Avatar, Card, IconTile, Link, Text, View } from '@tallo/design-system';
import { teamMemberName } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { buildingRoute } from '@tallo/investor/navigation';

import { BuildingDto } from '../dtos/building.dto.interface';
import { buildingAddressLine } from '../utils/building-address.util';
import { propertyManagerLabel } from '../utils/property-summary.util';
import PortfolioChip from './portfolio-chip.vue';

/** A building in the properties grid, beside the unit cards */
const props = defineProps<{
  building: BuildingDto;
  showPortfolio?: boolean;
  showManager?: boolean;
}>();
const emit = defineEmits<{ 'select-portfolio': [portfolioId: string] }>();

const { investor } = storeToRefs(useInvestorStore());

const managerName = computed(() => (props.building.manager ? teamMemberName(props.building.manager) : ''));
const managerLabel = computed(() => propertyManagerLabel(props.building, investor.value?.id));
</script>

<style scoped lang="scss">
.building-card {
  display: flex;
  flex-direction: column;
  gap: var(--t-spacing-md);
}

// A long name wraps inside the card rather than widening it
.identity,
.team {
  min-width: 0;
  overflow-wrap: anywhere;
}

.manager {
  min-width: 0;
}
</style>
